/**
 * The site's web maintenance package on its Saidiseire page: what the engine
 * does for the site because of it, and what the package leaves out
 * (docs/saidiseire-paketid.md).
 *
 * It sits right above the security, reporter and expiry cards because it
 * explains them: a package without the certificate look is why that card says
 * "not in the package". The speed test, the security scan and the monthly
 * report are ordinary lines here since they are built
 * (docs/saidiseire-kiirus-turve-raport.md); a site without a package gets none
 * of the three and is not told its package leaves them out.
 *
 * For a site on our own platform (item K) the WordPress-only lines (the plugin
 * and what it reads) are left out of both lists and a line says why: no plugin
 * runs there, the platform card shows the inside view.
 */
import { Package } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../../../i18n/I18nProvider";
import {
  planGone,
  planLabel,
  policyExcludes,
  policyIncludes,
  type MonitorPlanView,
  type MonitorPolicyView,
  type PolicyLine,
} from "../../../lib/monitoring";
import { platformPolicyLines } from "../../../lib/monitoringPlatform";
import { CardHead, StatusPill } from "../../ui";
import type { T } from "./words";

function lineText(t: T, line: PolicyLine): string {
  return t(`monitoring.policy.${line.key}`, line.vars);
}

export function PackageCard({
  planCode,
  policy,
  plans,
  canEditPlans,
  platform = false,
  t,
}: {
  planCode: string | null;
  /** The rules the engine applies to the site right now (`MonitorDetailResponse.policy`). */
  policy: MonitorPolicyView;
  /** The packages, for the name; null while they load or when they could not be read. */
  plans: readonly MonitorPlanView[] | null;
  /** The package editor is `manage:plans`; the link is drawn only for someone who can open it. */
  canEditPlans: boolean;
  /** The site runs on our own platform: the WordPress-only lines do not apply to it. */
  platform?: boolean;
  t: T;
}) {
  const { locale } = useI18n();
  const name = planLabel(plans, planCode, locale);
  const sub = !planCode
    ? t("monitoring.policy.none")
    : planGone(plans, planCode)
      ? t("monitoring.policy.gone", { code: planCode })
      : t("monitoring.policy.sub");
  const includes = platform ? platformPolicyLines(policyIncludes(policy)) : policyIncludes(policy);
  const excludes = platform ? platformPolicyLines(policyExcludes(policy)) : policyExcludes(policy);

  return (
    <section className="crm-card mb-4">
      <CardHead
        title={
          <span className="inline-flex items-center gap-2">
            <Package className="size-4 text-muted-foreground" strokeWidth={1.8} />
            {t("monitoring.policy.title")}
            <StatusPill tone={policy.source === "plan" ? "info" : "mut"}>{name ?? t("monitoring.policy.noneName")}</StatusPill>
          </span>
        }
        sub={sub}
        actions={
          canEditPlans ? (
            <Link to="/plans" className="crm-mut text-[13px] hover:text-foreground hover:underline">
              {t("monitoring.policy.edit")}
            </Link>
          ) : null
        }
      />
      <div className="space-y-2 px-[26px] pb-5 text-[13px]">
        <div className="flex flex-wrap gap-1.5">
          {includes.map((line) => (
            <span key={line.key} className="crm-chip">
              {lineText(t, line)}
            </span>
          ))}
        </div>
        {excludes.length > 0 ? (
          <p className="crm-mut">{t("monitoring.policy.excludes", { list: excludes.map((l) => lineText(t, l)).join(", ") })}</p>
        ) : null}
        {platform ? <p className="crm-mut">{t("monitoring.platform.policyNote")}</p> : null}
      </div>
    </section>
  );
}
